/**
 * Kleda Client App — Reparatie Offerte View
 * Route: /reparatie/:token
 *
 * Klant ziet voorstel (foto's per kleed + opdracht-tekst + totaalprijs)
 * en kan accepteren via één knop. Bij akkoord triggert Apex de factuurregel
 * + WO-status-revert (via Flow).
 */

import { useEffect, useState } from 'react'
import { PageLayout } from './Layout'

interface ReparatieItem {
  wiId: string
  wiNaam: string
  opdrachtTekst: string
  fotoUrl: string | null
  fotosTimelineUrl: string | null
  isKleed: boolean
  vorm: string | null
  afmetingen: string | null
}

interface ReparatieData {
  opdrachtId: string
  opdrachtNummer: string
  status: string
  klantNaam: string | null
  reparateurNaam: string | null
  opdrachtAlgemeen: string | null
  verkoopBedrag: number
  alreadyAccepted: boolean
  alreadyDeclined: boolean
  doorlooptijdWerkdagen: number | null
  voorbeeldToelichting: string | null
  voorbeeldFotoUrls: string[]
  items: ReparatieItem[]
  /** Verrijkt door de office-passthrough: kleed nog niet opgehaald bij de klant. */
  kleedNogBijKlant?: boolean
}

interface ApiEnvelope<T> {
  success: boolean
  data?: T
  error?: { code: string; message: string }
}

function formatPrice(amount: number): string {
  return `€ ${amount.toFixed(2).replace('.', ',')}`
}

interface Props {
  token: string
}

export function ReparatieOfferteView({ token }: Props) {
  const [data, setData] = useState<ReparatieData | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [accepted, setAccepted] = useState(false)
  const [declined, setDeclined] = useState(false)
  const [decliningOpen, setDecliningOpen] = useState(false)
  const [declineReason, setDeclineReason] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    fetch(`/api/client-app/reparatie?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const json = (await r.json()) as ApiEnvelope<ReparatieData>
        if (cancelled) return
        if (!json.success || !json.data) {
          setLoadError(json.error?.message || 'Kon offerte niet laden')
        } else {
          setData(json.data)
          if (json.data.alreadyAccepted) setAccepted(true)
          if (json.data.alreadyDeclined) setDeclined(true)
        }
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : 'Onbekende fout')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const handleAccept = async () => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const r = await fetch('/api/client-app/reparatie-akkoord', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, confirm: true }),
      })
      const json = (await r.json()) as ApiEnvelope<{ status: string; alreadyAccepted: boolean }>
      if (!json.success) {
        setSubmitError(json.error?.message || 'Akkoord mislukt')
        setSubmitting(false)
        return
      }
      setAccepted(true)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Akkoord mislukt')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDecline = async () => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const r = await fetch('/api/client-app/reparatie-akkoord', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, confirm: false, reden: declineReason.trim() || undefined }),
      })
      const json = (await r.json()) as ApiEnvelope<{ status: string; alreadyDeclined: boolean }>
      if (!json.success) {
        setSubmitError(json.error?.message || 'Afwijzen mislukt')
        setSubmitting(false)
        return
      }
      setDeclined(true)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Afwijzen mislukt')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <PageLayout>
        <div className="py-2xl text-center text-ink-muted">Voorstel laden…</div>
      </PageLayout>
    )
  }

  if (loadError || !data) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto py-xl">
          <div className="bg-white rounded-card p-lg border border-border-subtle text-center">
            <h1 className="text-heading-sub text-ink mb-sm">Hmm, dat lukte niet</h1>
            <p className="text-body text-ink-muted">
              {loadError || 'Kon dit voorstel niet vinden. Misschien is de link verlopen?'}
            </p>
            <p className="text-body-sm text-ink-muted mt-md">
              Bel ons op{' '}
              <a href="tel:+31882999277" className="text-primary underline">
                088 299 9277
              </a>{' '}
              dan helpen we je verder.
            </p>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout orderNummer={data.opdrachtNummer}>
      <div className="max-w-2xl mx-auto py-lg space-y-lg">
        {/* Intro */}
        <div className="text-center">
          <span className="inline-block text-body-sm font-medium text-primary bg-primary/10 px-md py-xs rounded-full mb-sm">
            Voorstel voor reparatie
          </span>
          <h1 className="text-heading-sub-lg text-ink mb-sm">
            Hoi{data.klantNaam ? ` ${data.klantNaam.split(' ')[0]}` : ''},
          </h1>
          <p className="text-body text-ink-muted">
            We hebben je vloerkleed bekeken en een vakman gevonden die de reparatie voor je kan doen. Hieronder vind je het voorstel.
          </p>
        </div>

        {/* Per kleed */}
        {data.items.length > 0 && (
          <div className="space-y-md">
            {data.items.map((item) => (
              <div
                key={item.wiId}
                className="bg-white rounded-card p-md border border-border-subtle flex gap-md"
              >
                {item.fotoUrl ? (
                  item.fotosTimelineUrl ? (
                    <a
                      href={item.fotosTimelineUrl}
                      target="_blank"
                      rel="noreferrer"
                      title="Bekijk alle foto's"
                      className="block flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32 rounded-md overflow-hidden bg-canvas border border-border-subtle"
                    >
                      <img src={item.fotoUrl} alt={item.wiNaam} className="w-full h-full object-cover" />
                    </a>
                  ) : (
                    <div className="block flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32 rounded-md overflow-hidden bg-canvas border border-border-subtle">
                      <img src={item.fotoUrl} alt={item.wiNaam} className="w-full h-full object-cover" />
                    </div>
                  )
                ) : (
                  <div className="flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32 rounded-md bg-canvas border border-border-subtle flex items-center justify-center text-ink-muted text-body-sm">
                    Geen foto
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-body-intro font-medium text-ink mb-xs">{item.wiNaam}</div>
                  {(item.vorm || item.afmetingen) && (
                    <div className="text-body-sm text-ink-muted mb-xs">
                      {[item.vorm, item.afmetingen].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {item.opdrachtTekst && (
                    <p className="text-body text-ink-muted whitespace-pre-wrap">{item.opdrachtTekst}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Zo wordt het ongeveer — voorbeeld-foto's van de vakman. Alleen tonen mét
            foto's: de toelichting hoort bij de foto's; losse reparateur-notities
            (bv. "ex btw") zijn zonder foto's verwarrend voor de klant. */}
        {data.voorbeeldFotoUrls.length > 0 && (
          <div className="bg-white rounded-card p-md border border-border-subtle">
            <h2 className="text-heading-card font-medium text-ink mb-xs">Zo wordt het ongeveer</h2>
            {data.voorbeeldToelichting && (
              <p className="text-body text-ink-muted mb-md">{data.voorbeeldToelichting}</p>
            )}
            {data.voorbeeldFotoUrls.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-sm">
                {data.voorbeeldFotoUrls.map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="block aspect-square rounded-md overflow-hidden bg-canvas border border-border-subtle"
                  >
                    <img src={url} alt={`Voorbeeld ${i + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Prijs + CTA */}
        <div className="bg-white rounded-card p-lg border border-border-subtle">
          <div className="text-center mb-md">
            <p className="text-body-sm text-ink-muted uppercase tracking-wide mb-xs">Reparatie totaal</p>
            <p className="text-heading-main text-ink font-bold">{formatPrice(data.verkoopBedrag)}</p>
            <p className="text-body-sm text-ink-muted mt-xs">
              incl. BTW · gratis ophalen en bezorgen
              {data.doorlooptijdWerkdagen != null
                ? ` · klaar in ± ${data.doorlooptijdWerkdagen} werkdag${data.doorlooptijdWerkdagen === 1 ? '' : 'en'}`
                : ''}
            </p>
          </div>

          {accepted ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-md text-center">
              <p className="text-body-intro font-medium text-emerald-700 mb-xs">Bedankt!</p>
              <p className="text-body text-ink-muted">
                {data.kleedNogBijKlant === true
                  ? 'Je voorstel is geaccepteerd. We halen je kleed eerst op, sturen het na het wassen naar onze vakman en bezorgen het gerepareerd terug.'
                  : data.kleedNogBijKlant === false
                    ? 'Je voorstel is geaccepteerd. Je kleed is bij ons; na het wassen gaat het naar onze vakman en we bezorgen het gerepareerd terug.'
                    : 'Je voorstel is geaccepteerd. We zorgen dat je kleed bij onze vakman komt en bezorgen het gerepareerd terug.'}
              </p>
            </div>
          ) : declined ? (
            <div className="bg-canvas border border-border-subtle rounded-md p-md text-center">
              <p className="text-body-intro font-medium text-ink mb-xs">Bedankt voor het doorgeven</p>
              <p className="text-body text-ink-muted">
                Geen probleem — we leveren je vloerkleed gewoon terug, zonder reparatie.
              </p>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={handleAccept}
                disabled={submitting}
                className="w-full py-md px-lg bg-primary hover:bg-primary-dark text-white font-medium rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Bezig…' : 'Ja, ga door met de reparatie'}
              </button>

              {!decliningOpen ? (
                <button
                  type="button"
                  onClick={() => setDecliningOpen(true)}
                  disabled={submitting}
                  className="w-full mt-sm py-md px-lg border border-border-subtle text-ink-muted font-medium rounded-full hover:bg-canvas transition-colors disabled:opacity-50"
                >
                  Nee, liever niet
                </button>
              ) : (
                <div className="mt-sm border border-border-subtle rounded-md p-md text-left">
                  <label className="block text-body-sm text-ink-muted mb-xs">
                    Mogen we kort weten waarom? (optioneel)
                  </label>
                  <textarea
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    rows={2}
                    maxLength={255}
                    autoFocus
                    placeholder="Bijv. te duur, liever niet nu…"
                    className="w-full text-body border border-border-subtle rounded-md px-sm py-xs"
                  />
                  <div className="flex gap-sm mt-sm">
                    <button
                      type="button"
                      onClick={handleDecline}
                      disabled={submitting}
                      className="flex-1 py-sm px-md bg-ink hover:bg-ink-soft text-white font-medium rounded-full transition-colors disabled:opacity-50"
                    >
                      {submitting ? 'Bezig…' : 'Bevestig: geen reparatie'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDecliningOpen(false)}
                      disabled={submitting}
                      className="py-sm px-md text-ink-muted hover:text-ink"
                    >
                      Terug
                    </button>
                  </div>
                </div>
              )}

              {submitError && <p className="text-body-sm text-red-600 mt-sm text-center">{submitError}</p>}
              <p className="text-body-sm text-ink-muted mt-md text-center">
                Vragen? Bel{' '}
                <a href="tel:+31882999277" className="text-primary underline">
                  088 299 9277
                </a>{' '}
                of mail{' '}
                <a href="mailto:info@kleda.nl" className="text-primary underline">
                  info@kleda.nl
                </a>
                .
              </p>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  )
}
